import { useFormContext, useWatch } from 'react-hook-form';

import { DynamicFormControl } from '@/components/DynamicFormControl';

import { AcronisCategoryOption } from '../../AcronisTopics/AcronisTopics.types';
import DynamicFormField from '../../DynamicFormField';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import SOURCE_TYPE from '../../SourceCreate.constants';
import { SourceDialogMode } from '../../SourceCreate.types';
import type { DynamicFieldsListProps } from './DynamicFieldsList.types';

/**
 * DynamicFieldsList renders dynamic fields from the connection schema.
 * Fields vary by source type and are fetched from backend.
 * Excludes service_detail_name and site_name as they're rendered separately.
 * Disables ingest_from field in Edit mode to prevent changing historical data start date.
 */
const DynamicFieldsList = ({
  sourceInputs,
  selectedSourceTypeId,
  mode,
  isResettingSourceType
}: DynamicFieldsListProps) => {
  const { formState, control } = useFormContext();
  const { errors } = formState;
  const selectedCategories = useWatch({ control, name: 'category' }) || [];

  // For Acronis, ingest_from is optional only when Event Manager is the only selected category.
  const isAcronisEventManagerSelected =
    selectedSourceTypeId === SOURCE_TYPE.ACRONIS &&
    selectedCategories.includes(AcronisCategoryOption.EventManager);
  const isAcronisEventManagerOnlySelected =
    isAcronisEventManagerSelected && selectedCategories.length === 1;

  // Don't render any fields while resetting source type
  if (isResettingSourceType) return null;

  const getGridSize = (fieldName: string, sourceType: number) => ({
    sm:
      sourceType === SOURCE_TYPE.AWS_CLOUDTRAIL && fieldName === 'region'
        ? 12
        : 6,
    md:
      sourceType === SOURCE_TYPE.AWS_CLOUDTRAIL && fieldName === 'region'
        ? 12
        : 4
  });

  return (
    <>
      {sourceInputs
        ?.filter(
          ({ name }) => name !== 'service_detail_name' && name !== 'site_name'
        )
        .map(({ ...field }) => {
          const { sm, md } = getGridSize(field?.name, selectedSourceTypeId);
          const isIngestFromField = field?.name === 'ingest_from';
          const isIngestFromOptionalForAcronisEventManager =
            isIngestFromField && isAcronisEventManagerOnlySelected;
          const isFieldDisabled =
            isIngestFromField && mode === SourceDialogMode.Edit;

          const updatedField = {
            ...field,
            nonMandatory: isIngestFromOptionalForAcronisEventManager
              ? true
              : field?.nonMandatory,
            config: isIngestFromOptionalForAcronisEventManager
              ? {
                  ...(field?.config ?? {}),
                  required: false
                }
              : field?.config,
            disabled: isFieldDisabled
          };

          return (
            <DynamicFormField
              key={`${selectedSourceTypeId}-${updatedField?.name}`}
              formField={updatedField}
              xs={12}
              sm={sm}
              md={md}
            >
              <div>
                <DynamicFormControl {...updatedField} />
                {errors[field?.name] && (
                  <ErrorMessage message={errors[field?.name]?.message} />
                )}
              </div>
            </DynamicFormField>
          );
        })}
    </>
  );
};

export default DynamicFieldsList;
